// src/components/TripTimeline.jsx
// Bottom card slider for trip visualization — swipe through stops, map follows
import { useState, useRef, useEffect, useCallback } from "react";

const FL = "'Manrope', -apple-system, sans-serif";
const TRIP_COLOR = "#E8943A";
const CARD_W_BASE = 260;
const CARD_GAP = 10;

export default function TripTimeline({ trip, onSelectStop, onClose, bottomOffset = 70, leftOffset = 0 }) {
  const [activeIdx, setActiveIdx] = useState(0);
  const scrollRef = useRef(null);
  const isScrolling = useRef(false);
  const stops = trip?.stops || [];
  const CARD_W = Math.min(CARD_W_BASE, (typeof window !== "undefined" ? window.innerWidth : 400) - 40);

  // Notify parent when active stop changes
  useEffect(() => {
    if (stops[activeIdx]) onSelectStop?.(stops[activeIdx], activeIdx);
  }, [activeIdx]); // eslint-disable-line

  // Handle scroll snap to detect which card is centered
  const handleScroll = useCallback(() => {
    if (isScrolling.current) return;
    const el = scrollRef.current;
    if (!el) return;
    const scrollLeft = el.scrollLeft;
    const idx = Math.round(scrollLeft / (CARD_W + CARD_GAP));
    const clamped = Math.max(0, Math.min(idx, stops.length - 1));
    if (clamped !== activeIdx) setActiveIdx(clamped);
  }, [activeIdx, stops.length]);

  // Scroll to a specific card
  const scrollToIdx = (idx) => {
    const el = scrollRef.current;
    if (!el) return;
    isScrolling.current = true;
    el.scrollTo({ left: idx * (CARD_W + CARD_GAP), behavior: "smooth" });
    setActiveIdx(idx);
    setTimeout(() => { isScrolling.current = false; }, 400);
  };

  if (!stops.length) return null;

  return (
    <div style={{
      position: "fixed",
      bottom: bottomOffset, left: leftOffset, right: 0,
      zIndex: 30,
      pointerEvents: "none",
    }}>
      {/* Progress dots */}
      <div style={{
        display: "flex", justifyContent: "center", gap: 4,
        marginBottom: 8, pointerEvents: "auto",
      }}>
        {stops.map((_, i) => (
          <button
            key={i}
            onClick={() => scrollToIdx(i)}
            style={{
              width: i === activeIdx ? 16 : 6,
              height: 6,
              borderRadius: 3,
              background: i === activeIdx ? TRIP_COLOR : "rgba(101,93,84,0.3)",
              border: "none",
              padding: 0,
              cursor: "pointer",
              transition: "width 0.2s, background 0.2s",
            }}
          />
        ))}
      </div>

      {/* Scrollable cards */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        style={{
          display: "flex",
          gap: CARD_GAP,
          overflowX: "auto",
          scrollSnapType: "x mandatory",
          scrollbarWidth: "none",
          paddingLeft: `calc(50% - ${CARD_W / 2}px)`,
          paddingRight: `calc(50% - ${CARD_W / 2}px)`,
          paddingBottom: 4,
          pointerEvents: "auto",
        }}
      >
        {stops.map((stop, i) => (
          <div
            key={i}
            onClick={() => scrollToIdx(i)}
            style={{
              width: CARD_W,
              flexShrink: 0,
              scrollSnapAlign: "center",
              background: i === activeIdx ? "#fff" : "rgba(255,255,255,0.85)",
              borderRadius: 14,
              border: i === activeIdx ? `2px solid ${TRIP_COLOR}` : "2px solid transparent",
              boxShadow: i === activeIdx
                ? "0 6px 24px rgba(232,148,58,0.25)"
                : "0 2px 12px rgba(0,0,0,0.08)",
              overflow: "hidden",
              cursor: "pointer",
              transition: "border 0.2s, box-shadow 0.2s",
            }}
          >
            <div style={{ display: "flex", gap: 0 }}>
              {/* Thumbnail */}
              {stop.thumbnailUrl && (
                <div style={{
                  width: 80, height: 80, flexShrink: 0,
                  backgroundImage: `url('${stop.thumbnailUrl}')`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }} />
              )}

              {/* Info */}
              <div style={{
                flex: 1, padding: "10px 12px",
                display: "flex", flexDirection: "column", justifyContent: "center",
                minWidth: 0,
              }}>
                {/* Stop number + time */}
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                  <span style={{
                    width: 20, height: 20, borderRadius: "50%",
                    background: TRIP_COLOR, color: "#fff",
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    fontFamily: FL, fontSize: 10, fontWeight: 800,
                    flexShrink: 0,
                  }}>{i + 1}</span>
                  {stop.time && (
                    <span style={{
                      fontFamily: FL, fontSize: 10, color: "#8a8e8a", fontWeight: 600,
                    }}>{stop.time}</span>
                  )}
                </div>

                {/* Place name */}
                <p style={{
                  margin: 0, fontFamily: FL, fontSize: 13, fontWeight: 700,
                  color: "#2f3430",
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                  {stop.name || `Stop ${i + 1}`}
                </p>

                {/* Memo */}
                {stop.memo && (
                  <p style={{
                    margin: "2px 0 0", fontFamily: "'Noto Serif', Georgia, serif", fontStyle: "italic",
                    fontSize: 11, color: "#5c605c",
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>"{stop.memo}"</p>
                )}

                {/* Nav arrows */}
                <div style={{ display: "flex", gap: 4, marginTop: 6 }}>
                  {i > 0 && (
                    <button
                      onClick={(e) => { e.stopPropagation(); scrollToIdx(i - 1); }}
                      style={{
                        background: "#f4f4f0", border: "none", borderRadius: 4,
                        padding: "2px 6px", cursor: "pointer",
                        fontFamily: FL, fontSize: 10, color: "#655d54",
                      }}
                    >← prev</button>
                  )}
                  {i < stops.length - 1 && (
                    <button
                      onClick={(e) => { e.stopPropagation(); scrollToIdx(i + 1); }}
                      style={{
                        background: "#f4f4f0", border: "none", borderRadius: 4,
                        padding: "2px 6px", cursor: "pointer",
                        fontFamily: FL, fontSize: 10, color: "#655d54",
                      }}
                    >next →</button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
